import React from 'react';
import {
  WHATSAPP_TEMPLATE_BG_COLOR,
  WHATSAPP_TEMPLATE_UNDERLAY_COLOR
} from '../../config/constants';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import './template.css';
import { WHATSAPP_TEMPLATE_STATUS } from '../communication/constants';
import { useTranslate } from '@tolgee/react';
import { getTimeFormated } from '../utils/dates/utils';

interface ComponentType {
  type: string;
  format?: string;
  text?: string;
  example?: {
    header_text?: string[];
    body_text?: string[][];
    body_text_named_params?: any[];
    header_text_named_params?: any[];
    header_handle?: string[];
  };
  buttons?: { type: string; text: string; url?: string }[];
}

interface TemplateProps {
  template: {
    id: string;
    name: string;
    language: string;
    category: string;
    status: string;
    components: ComponentType[];
  };
  cardOnly: boolean;
}

// Function to replace placeholders {{1}}, {{2}}, etc.
const replacePlaceholders = (text: string, values?: any[]) => {
  if (!values) return text;

  return text.split(/({{\d+}}|{{[\w_]+}})/g).map((part, index) => {
    const match = part.match(/{{(\d+|[\w_]+)}}/);
    if (!match) return part;

    const key = match[1];

    // Check for numeric placeholders (e.g., {{1}}, {{2}})
    const numericValue = values[parseInt(key, 10) - 1];
    if (numericValue) return numericValue;

    // Check for named parameters (e.g., {{some_constant}})
    const namedValues = values
      .filter((p) => p.param_name === key || p.parameter_name === key)
      .map((p) => p.example)
      .join(', '); // Join multiple values with a comma or another separator

    if (namedValues) return namedValues;

    // Default case: return placeholder wrapped in green text
    return (
      <span key={index} style={{ color: 'green' }}>
        {`{{${key}}}`}
      </span>
    );
  });
};
const TemplateCard: React.FC<TemplateProps> = ({
  template,
  cardOnly = false
}) => {
  const data = template;
  const { t } = useTranslate();
  const header = data.components.find((c) => c.type === 'HEADER');
  const body = data.components.find((c) => c.type === 'BODY');
  const footer = data.components.find((c) => c.type === 'FOOTER');
  const buttons =
    data.components.find((c) => c.type === 'BUTTONS')?.buttons || [];

  const cardContent = (
    <Card.Body
      style={{}}
      className="pt-3 bg-white message-card position-relative d-flex flex-column gap-1 rounded-2 ml-1 w-100 px-0 cursor-pointer"
    >
      {/* Header Section */}
      {header && (
        <Card.Title className="fw-bold px-4 pt-2">
          {header.format === 'TEXT' &&
            replacePlaceholders(
              header.text || '',
              header.example?.header_text ||
                header.example?.header_text_named_params
            )}

          {header.format === 'IMAGE' && header.example?.header_handle && (
            <img
              src={header.example.header_handle[0]}
              alt="Template Header"
              className="img-fluid rounded"
            />
          )}
          {header.format === 'VIDEO' && header.example?.header_handle && (
            <video controls className="w-100">
              <source src={header.example.header_handle[0]} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </Card.Title>
      )}

      {/* Body Section */}
      {body && (
        <Card.Text className="mb-2 px-4 w-100">
          {replacePlaceholders(
            body.text || '',
            body.example?.body_text?.[0] ||
              body?.example?.body_text_named_params
          )}
        </Card.Text>
      )}

      {/* Footer Section */}
      {footer && <div className="text-muted px-3 w-100">{footer.text}</div>}

      {/* Timestamp */}
      <div
        className="text-end text-muted mt-1 px-2"
        style={{ fontSize: '12px' }}
      >
        {getTimeFormated(new Date())}
      </div>

      {/* Action Buttons */}
      <div className="mt-3 w-100 border-bottom">
        {buttons.map((btn, index) => (
          <Button
            key={index}
            variant={'link'}
            className="me-2 text-primary d-flex justify-content-center align-items-center border-top w-100"
            href={btn.type === 'URL' ? btn.url : undefined}
            target={btn.type === 'URL' ? '_blank' : undefined}
            rel={btn.type === 'URL' ? 'noopener noreferrer' : undefined}
          >
            {btn.type === 'URL' && (
              <i className="fa-solid fa-up-right-from-square text-primary"></i>
            )}
            {btn.text}
          </Button>
        ))}
      </div>
    </Card.Body>
  );

  if (cardOnly) {
    return (
      <div className={'mx-2'} style={{ height: 'auto' }}>
        {cardContent}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100 mx-2" style={{ height: '100%' }}>
      <div className="w-100 my-2 d-flex flex-fill flex-column bg-white border rounded-2">
        <div className="d-flex flex-column w-100 flex-fill">
          <div className="position-absolute d-flex justify-content-end">
            <div
              style={{ zIndex: 9 }}
              className={`badge ${
                data?.status === WHATSAPP_TEMPLATE_STATUS.PENDING
                  ? 'bg-warning'
                  : data?.status === WHATSAPP_TEMPLATE_STATUS.REJECTED
                  ? 'bg-danger'
                  : ''
              }  mb-1`}
            >
              {data?.status === WHATSAPP_TEMPLATE_STATUS.PENDING
                ? t('communication.whatsappTemplate.pending')
                : data?.status === WHATSAPP_TEMPLATE_STATUS.REJECTED
                ? t('communication.whatsappTemplate.rejected')
                : '.'}
            </div>
          </div>
          <div
            style={{
              backgroundColor: WHATSAPP_TEMPLATE_BG_COLOR,
              boxShadow: '0px 2px 5px rgba(0,0,0,0.1)'
            }}
            className="w-100 d-flex flex-fill rounded-top px-5 py-4"
          >
            <div>{cardContent}</div>
          </div>
        </div>
        <div>
          {data?.status == 'APPROVED' && (
            <div className={'w-100 d-flex justify-content-end mt-n5'}>
              <div
                style={{ zIndex: 9 }}
                className={`badge bg-secondary text-dark`}
              >
                {data?.category}
              </div>
            </div>
          )}
          <div
            className="bg-white p-2 rounded-bottom text-muted"
            style={{ lineBreak: 'anywhere' }}
          >
            {data?.name}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
